import type {
  AuditItemResumo,
  ChatAuditActorKind,
  ChatAuditListItem,
  ChatAuditOperacao,
} from '../types';

export const OPERACOES_PERTINENTES: readonly ChatAuditOperacao[] = [
  'CHAT_CONVERSA_ASSUMIDA',
  'CHAT_CONVERSA_TRANSFERIDA',
  'CHAT_RESPONSAVEL_ALTERADO',
  'CHAT_CONVERSA_FECHADA',
  'CHAT_CONVERSA_DESARQUIVADA',
  'CHAT_BOT_RETOMADO',
  'CHAT_HANDOFF_HUMANO',
];

const OPERACAO_LABELS: Record<string, string> = {
  CHAT_MENSAGEM_ENVIADA: 'enviou mensagem',
  CHAT_MENSAGEM_INBOUND: 'enviou mensagem',
  CHAT_CONVERSA_ASSUMIDA: 'assumiu a conversa',
  CHAT_CONVERSA_TRANSFERIDA: 'transferiu a conversa',
  CHAT_RESPONSAVEL_ALTERADO: 'alterou o responsável',
  CHAT_CONVERSA_FECHADA: 'encerrou a conversa',
  CHAT_CONVERSA_DESARQUIVADA: 'reabriu a conversa',
  CHAT_BOT_RETOMADO: 'reativou o bot',
  CHAT_HANDOFF_HUMANO: 'transferiu para atendimento humano',
  CHAT_EXPORT_PDF_SOLICITADO: 'solicitou exportação da conversa em PDF',
  CHAT_EXPORT_PDF_CONCLUIDO: 'exportação em PDF concluída',
  CHAT_EXPORT_PDF_FALHOU: 'exportação em PDF falhou',
};

const ACTOR_LABELS: Record<ChatAuditActorKind, string> = {
  HUMAN: 'Atendente',
  BOT: 'Bot',
  SYSTEM: 'Sistema',
  WEBHOOK: 'Cliente',
  JOB: 'Automação',
};

const TIPO_MENSAGEM_LABELS: Record<string, string> = {
  TEXT: 'Texto',
  AUDIO: 'Áudio',
  IMAGE: 'Imagem',
  DOCUMENT: 'Documento',
};

export const labelForOperacao = (operacao: string): string => OPERACAO_LABELS[operacao] ?? operacao;

export const labelForActor = (item: ChatAuditListItem): string => {
  if (item.actorKind === 'HUMAN') {
    return item.usuarioNome?.trim() || 'Atendente';
  }
  return ACTOR_LABELS[item.actorKind] ?? item.actorKind;
};

const texto = (fonte: Record<string, unknown> | null, key: string): string | null => {
  const value = fonte?.[key];
  return typeof value === 'string' && value.trim() ? value : null;
};

const nomeUsuario = (item: ChatAuditListItem, id: string | null): string | null =>
  (id && item.nomesUsuarios?.[id]) || null;

const resumoMensagem = (item: ChatAuditListItem): AuditItemResumo => {
  const tipo = texto(item.dadosNovos, 'type');
  return {
    acao: labelForOperacao(item.operacao),
    detalhe: tipo ? (TIPO_MENSAGEM_LABELS[tipo.toUpperCase()] ?? tipo.toLowerCase()) : null,
  };
};

const resumoTransferida = (item: ChatAuditListItem): AuditItemResumo => {
  const destino = nomeUsuario(item, texto(item.dadosNovos, 'to'));
  return {
    acao: destino ? `transferiu a conversa para ${destino}` : 'transferiu a conversa',
    detalhe: texto(item.dadosNovos, 'motivo'),
  };
};

const resumoResponsavelAlterado = (item: ChatAuditListItem): AuditItemResumo => {
  const de = nomeUsuario(item, texto(item.dadosAnteriores, 'from'));
  const para = nomeUsuario(item, texto(item.dadosNovos, 'to'));
  const detalhe = de && para ? `De ${de} para ${para}` : para ? `Para ${para}` : null;
  return { acao: 'alterou o responsável', detalhe };
};

export const summarizeAuditItem = (item: ChatAuditListItem): AuditItemResumo => {
  switch (item.operacao) {
    case 'CHAT_MENSAGEM_ENVIADA':
    case 'CHAT_MENSAGEM_INBOUND':
      return resumoMensagem(item);
    case 'CHAT_CONVERSA_TRANSFERIDA':
      return resumoTransferida(item);
    case 'CHAT_RESPONSAVEL_ALTERADO':
      return resumoResponsavelAlterado(item);
    case 'CHAT_CONVERSA_ASSUMIDA':
      return { acao: labelForOperacao(item.operacao), detalhe: texto(item.dadosNovos, 'reason') };
    case 'CHAT_HANDOFF_HUMANO': {
      const reason = texto(item.dadosNovos, 'reason') ?? texto(item.dadosNovos, 'summary');
      if (reason && /retomada/i.test(reason)) {
        return { acao: 'enviou template de retomada', detalhe: 'janela de 24h' };
      }
      return { acao: labelForOperacao(item.operacao), detalhe: reason };
    }
    case 'CHAT_CONVERSA_FECHADA': {
      const motivo = texto(item.dadosNovos, 'motivo');
      if (motivo === 'INATIVIDADE') {
        return { acao: 'encerrou a conversa', detalhe: 'por inatividade' };
      }
      if (motivo === 'MANUAL') {
        return { acao: 'encerrou a conversa', detalhe: 'manualmente' };
      }
      return { acao: 'encerrou a conversa', detalhe: motivo };
    }
    default:
      return { acao: labelForOperacao(item.operacao), detalhe: null };
  }
};

/** Day key for grouping (local calendar date). */
export const auditDayKey = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const formatAuditDayLabel = (iso: string, now = new Date()): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startOfToday.getTime() - startOfDay.getTime()) / 86_400_000);

  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Ontem';

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

export const formatAuditTime = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Absolute datetime for tooltips / a11y. */
export const formatAuditWhen = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export interface AuditDayGroup {
  dayKey: string;
  label: string;
  items: ChatAuditListItem[];
}

export const groupAuditItemsByDay = (
  items: ChatAuditListItem[],
  now = new Date(),
): AuditDayGroup[] => {
  const groups = new Map<string, AuditDayGroup>();
  for (const item of items) {
    const dayKey = auditDayKey(item.dataOperacao);
    let group = groups.get(dayKey);
    if (!group) {
      group = {
        dayKey,
        label: formatAuditDayLabel(item.dataOperacao, now),
        items: [],
      };
      groups.set(dayKey, group);
    }
    group.items.push(item);
  }
  return Array.from(groups.values());
};
